import React, { useState, useMemo, useEffect } from 'react';
import 'react-confirm-alert/src/react-confirm-alert.css';
import '../CampanaInfo/CampanaInfo.css';

import { useAuth } from '../AuthContext/AuthContext';
import { getArticles } from '../services/articles';
import { executeVote } from '../services/executeVote';

export default function PantallaVotacion(props) {
    const { getAvailableVotes, candidates, askForVote } = props;
    const { dispatch } = useAuth();
    const [articulosData, setArticulosData] = useState([]);

    // Obtener los artículos reales de la API
    useEffect(() => {
        getArticles(dispatch.token)
            .then(response => {
                if (response && response.statusCode === 200 && response.body && response.body.items) {
                    setArticulosData(response.body.items);
                }
            })
            .catch(error => console.error("Error obteniendo artículos:", error));
    }, [dispatch.token]);

    // Vincular la data de candidatos y artículos en un solo arreglo estructurado
    const preguntasIniciales = useMemo(() => {
        if (!articulosData || articulosData.length === 0) return [];

        const estadosMap = {};
        const respuestasMap = {};
        const descripcionesMap = {};
        if (candidates) {
            candidates.forEach(item => {
                estadosMap[item.IDCandidato] = item.estado;
                respuestasMap[item.IDCandidato] = item.respuesta || item.Respuesta;
                descripcionesMap[item.IDCandidato] = item.Descripcion;
            });
        }

        return articulosData.map((art) => {
            const idSi = art.opciones.si.idCandidato;
            const idNo = art.opciones.no.idCandidato;

            // Revisamos el estado de ambos candidatos para determinar si ya se votó
            const estadoSi = estadosMap[idSi] || 'Uninitiated';
            const estadoNo = estadosMap[idNo] || 'Uninitiated';

            let estadoVotacion = estadoSi;
            let votoSeleccionado = null;

            if (estadoSi === 'Voted') {
                estadoVotacion = 'Voted';
                votoSeleccionado = idSi;
            } else if (estadoNo === 'Voted') {
                estadoVotacion = 'Voted';
                votoSeleccionado = idNo;
            } else if (estadoSi === 'Exhausted' || estadoNo === 'Exhausted') {
                estadoVotacion = 'Exhausted';
            } else if (estadoSi === 'Finalized' || estadoNo === 'Finalized') {
                estadoVotacion = 'Finalized';
            } else if (estadoSi === 'Allow' || estadoNo === 'Allow') {
                estadoVotacion = 'Allow';
            }

            const respuestaCandidato = respuestasMap[idSi] || art.titulo;
            const descripcionCandidato = descripcionesMap[idSi] || '';

            return {
                id: art.numero,
                titulo: respuestaCandidato,
                Descripcion: descripcionCandidato,
                contenidoHtml: art.contenido,
                idCandidatoSi: idSi,
                idCandidatoNo: idNo,
                estado: estadoVotacion, // Uninitiated, Allow, Voted, Finalized
                votoSeleccionado: votoSeleccionado
            };
        });
    }, [candidates, articulosData]);

    const [preguntas, setPreguntas] = useState(preguntasIniciales);

    useEffect(() => {
        setPreguntas(preguntasIniciales);
    }, [preguntasIniciales]);

    const [indiceActual, setIndiceActual] = useState(0);
    const [seleccionTemporal, setSeleccionTemporal] = useState(null);

    if (preguntas.length === 0) {
        return (
            <div style={{ fontFamily: 'Arial, sans-serif', backgroundColor: '#f8f9fa', padding: '20px', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <h2 style={{ color: '#4a5568' }}>Cargando artículos para votación...</h2>
            </div>
        );
    }

    const preguntaActual = preguntas[indiceActual];

    // Manejadores de Navegación
    const irAAnterior = () => {
        if (indiceActual > 0) {
            setIndiceActual(indiceActual - 1);
            setSeleccionTemporal(null);
        }
    };

    const irASiguiente = () => {
        if (indiceActual < preguntas.length - 1) {
            setIndiceActual(indiceActual + 1);
            setSeleccionTemporal(null);
        }
    };

    const seleccionarPreguntaDirecta = (index) => {
        setIndiceActual(index);
        setSeleccionTemporal(null);
    };

    // Lógica para votar directamente al presionar SI o NO
    const votarDirecto = (candidateId) => {
        if (!candidateId) return;

        const nuevasPreguntas = preguntas.map((p, idx) => {
            if (idx === indiceActual) {
                return {
                    ...p,
                    estado: 'Voted',
                    votoSeleccionado: candidateId
                };
            }
            return p;
        });

        setPreguntas(nuevasPreguntas);
        setSeleccionTemporal(null);

        // Buscar la siguiente pregunta disponible (no votada, ni finalizada, ni agotada)
        const siguienteDisponible = nuevasPreguntas.findIndex(
            (p, idx) => idx > indiceActual && p.estado !== 'Voted' && p.estado !== 'Finalized' && p.estado !== 'Exhausted'
        );

        if (siguienteDisponible !== -1) {
            setIndiceActual(siguienteDisponible);
        }

        executeVote(candidateId, dispatch.cif, dispatch.token)
            .catch(error => console.error("Error ejecutando voto:", error));
    };

    // Validaciones de bloqueo de UI
    const yaVotoOFinalizo = preguntaActual.estado === 'Voted' || preguntaActual.estado === 'Finalized' || preguntaActual.estado === 'Exhausted';
    const estaDeshabilitadoAcceso = preguntaActual.estado === 'Uninitiated';

    return (
        <div style={{ fontFamily: 'Arial, sans-serif', backgroundColor: '#f8f9fa', padding: '20px', minHeight: '100vh' }}>

            {/* Encabezado Principal */}
            <header style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '15px', marginBottom: '25px' }}>
                <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#1a202c' }}>
                    {getAvailableVotes()}
                </h1>
            </header>

            {/* Control de Navegación por círculos (Map de Question) */}
            <div style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    {preguntas.map((p, idx) => {
                        const esActiva = idx === indiceActual;
                        const completada = p.estado === 'Voted' || p.estado === 'Finalized' || p.estado === 'Exhausted';

                        // Estilos dinámicos basados en la imagen image_8e3deb.jpg
                        let bgCircle = '#e2e8f0';
                        let textColor = '#718096';
                        let borderCircle = '1px solid #cbd5e0';

                        if (esActiva) {
                            bgCircle = '#1d4ed8';
                            textColor = '#ffffff';
                            borderCircle = '2px solid #1e3a8a';
                        } else if (completada) {
                            bgCircle = '#e6f4ea';
                            textColor = '#137333';
                            borderCircle = '1px solid #137333';
                        }

                        return (
                            <div key={p.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                                <button
                                    onClick={() => seleccionarPreguntaDirecta(idx)}
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        backgroundColor: bgCircle,
                                        color: textColor,
                                        border: borderCircle,
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {p.id}
                                    {completada && !esActiva && (
                                        <span style={{ fontSize: '10px', position: 'absolute', top: '-2px', right: '-2px', background: '#137333', color: '#fff', borderRadius: '50%', width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</span>
                                    )}
                                </button>
                                {esActiva && (
                                    <span style={{ fontSize: '11px', marginTop: '4px', color: '#1a202c', fontWeight: 'bold' }}>
                                        Pregunta {p.id}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Grid Principal Layout (Progreso lateral y Card central) */}
            <div style={{ display: 'grid' }}>

                {/* Card de la Pregunta / Votación actual */}
                <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>

                    {/* Indicador de Ubicación */}
                    <div style={{ backgroundColor: '#d9e2ec', padding: '8px 16px', textAlign: 'center', fontSize: '13px', color: '#334e68', fontWeight: '500' }}>
                        Ubicación actual: Pregunta {indiceActual + 1} de {preguntas.length}
                    </div>

                    <div style={{ padding: '24px' }}>

                        <h2>{preguntaActual.contenidoHtml && preguntaActual.Descripcion}</h2>
                        {/* Contenido HTML del Artículo (Inyectado de forma segura controlada) */}
                        {preguntaActual.contenidoHtml && (
                            <div
                                style={{
                                    backgroundColor: '#f0f4f8',
                                    padding: '16px',
                                    borderRadius: '6px',
                                    marginBottom: '24px',
                                    maxHeight: '280px',
                                    overflowY: 'auto',
                                    fontSize: '14px',
                                    lineHeight: '1.6',
                                    border: '1px solid #d9e2ec'
                                }}
                                dangerouslySetInnerHTML={{ __html: preguntaActual.contenidoHtml }}
                            />
                        )}
                        {/* Título de la pregunta */}
                        <h2 style={{ fontSize: '22px', color: '#102a43', marginTop: '0', marginBottom: '20px', lineHeight: '1.4' }}>
                            <span dangerouslySetInnerHTML={{ __html: preguntaActual.titulo }} />
                        </h2>
                        {/* Render de Opciones de Voto */}
                        {estaDeshabilitadoAcceso ? (
                            <div style={{ color: '#9b2c2c', backgroundColor: '#fff5f5', padding: '12px', borderRadius: '6px', marginBottom: '20px', fontWeight: '500' }}>
                                ⚠️ Esta votación aún no se encuentra iniciada o disponible.
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>

                                {/* Botón Opción SI */}
                                <button
                                    disabled={yaVotoOFinalizo}
                                    onClick={() => votarDirecto(preguntaActual.idCandidatoSi)}
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: '8px',
                                        border: (seleccionTemporal === preguntaActual.idCandidatoSi || (yaVotoOFinalizo && preguntaActual.votoSeleccionado === preguntaActual.idCandidatoSi))
                                            ? '2px solid #1a202c' : '2px solid transparent',
                                        background: 'linear-gradient(135deg, #5cb85c, #4cae4c)',
                                        color: '#ffffff',
                                        textAlign: 'center',
                                        justifyContent: 'center',
                                        cursor: yaVotoOFinalizo ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        opacity: yaVotoOFinalizo && preguntaActual.votoSeleccionado !== preguntaActual.idCandidatoSi ? 0.6 : 1
                                    }}
                                >
                                    SI
                                </button>

                                {/* Botón Opción NO */}
                                <button
                                    disabled={yaVotoOFinalizo}
                                    onClick={() => votarDirecto(preguntaActual.idCandidatoNo)}
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: '8px',
                                        border: (seleccionTemporal === preguntaActual.idCandidatoNo || (yaVotoOFinalizo && preguntaActual.votoSeleccionado === preguntaActual.idCandidatoNo))
                                            ? '2px solid #1a202c' : '2px solid transparent',
                                        backgroundColor: '#d9534f',
                                        color: '#ffffff',
                                        textAlign: 'center',
                                        justifyContent: 'center',
                                        cursor: yaVotoOFinalizo ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        opacity: yaVotoOFinalizo && preguntaActual.votoSeleccionado !== preguntaActual.idCandidatoNo ? 0.6 : 1
                                    }}
                                >
                                    NO
                                </button>
                            </div>
                        )}



                        {preguntaActual.estado === 'Voted' && (
                            <div style={{ color: '#2f855a', backgroundColor: '#f0fff4', padding: '10px 16px', borderRadius: '4px', marginBottom: '20px', fontSize: '14px', fontWeight: '500' }}>
                                ✓ Voto ya aplicado y guardado para este artículo. Las opciones se encuentran bloqueadas.
                            </div>
                        )}

                        {preguntaActual.estado === 'Exhausted' && (
                            <div style={{ color: '#9b2c2c', backgroundColor: '#fff5f5', padding: '10px 16px', borderRadius: '4px', marginBottom: '20px', fontSize: '14px', fontWeight: '500' }}>
                                ⚠️ Ya no tienes votos disponibles para esta categoría. Las opciones se encuentran bloqueadas.
                            </div>
                        )}



                    </div>
                </div>

            </div>

        </div>
    );
}